import { useState, useEffect, useCallback } from "react";
import { Row, Col, Typography } from "antd";
import {
  TrendingUp,
  ShoppingBag,
  RefreshCcw,
  DollarSign,
  TrendingDown,
  Ticket,
} from "lucide-react";
import { fetchCommunitySummary } from "services/ordersAndBookings.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import useAuthStore from "stores/AuthStore/AuthStore";
import PageLoader from "Components/PageLoader/PageLoader";

const { Text, Title } = Typography;

const CARDS = [
  {
    key: "total_orders",
    label: "Total Orders",
    icon: ShoppingBag,
    colorClass: "bg-blue-100 text-blue-600",
  },
  {
    key: "gross_revenue",
    label: "Gross Revenue",
    icon: DollarSign,
    colorClass: "bg-emerald-100 text-emerald-600",
    isCurrency: true,
  },
  {
    key: "revenue_current_month",
    label: "This Month",
    icon: TrendingUp,
    colorClass: "bg-violet-100 text-violet-600",
    isCurrency: true,
  },
  {
    key: "revenue_prior_month",
    label: "Last Month",
    icon: TrendingDown,
    colorClass: "bg-amber-100 text-amber-600",
    isCurrency: true,
  },
  {
    key: "refunded_count",
    label: "Refunds",
    icon: RefreshCcw,
    colorClass: "bg-red-100 text-red-600",
  },
  {
    key: "avg_ticket_price",
    label: "Avg. Ticket Price",
    icon: Ticket,
    colorClass: "bg-teal-100 text-teal-600",
    isCurrency: true,
  },
];

function AnalyticsSummary({ communityId }) {
  const { userData } = useAuthStore();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userData?.vendor_id) return;
    setLoading(true);
    const { data, errors } = await fetchCommunitySummary({
      vendor_id: userData.vendor_id,
      ...(communityId ? { community_id: communityId } : {}),
    });
    setLoading(false);
    if (errors?.length) {
      renderErrorNotifications(errors);
      return;
    }
    setSummary(data);
  }, [userData?.vendor_id, communityId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return <PageLoader showHero={false} showTabs={false} showCards={false} />;

  return (
    <Row gutter={[12, 12]}>
      {CARDS.map(({ key, label, icon: Icon, colorClass, isCurrency }) => (
        <Col key={key} xs={12} sm={8} lg={4}>
          <div className="rounded-2xl border border-border/50 bg-card p-5 shadow-card">
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 8 }}
            >
              <Col>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {label}
                </Text>
              </Col>
              <Col>
                <div className={`rounded-lg p-1.5 ${colorClass}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
              </Col>
            </Row>
            <Title level={4} style={{ margin: 0 }}>
              {isCurrency
                ? Number(summary?.[key] ?? 0).toLocaleString("en-IN", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })
                : (summary?.[key] ?? 0).toLocaleString()}
            </Title>
          </div>
        </Col>
      ))}
    </Row>
  );
}

export default AnalyticsSummary;
